import {
  AspectRatio,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FocusTrap,
  Image,
  Loader,
  Text,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import {
  IconBell,
  IconChevronLeft,
  IconChevronRight,
  IconEdit,
  IconMessages,
  IconNews,
  IconUserCircle,
} from '@tabler/icons-react'
import classes from './Header.module.css'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import UpdatedBar from './UpdatedBar'
import { useIsPlayer } from '../../api'
import {
  useNotificationsCount,
  Notification,
  useMarkAllAsRead,
  useNotificationsInfinite,
  useMarkAsRead,
} from '../../../notifications/api'
import { Typography } from '../../typography'

const COLLAPSE_AT = 8
const EXPAND_AT = 2

function useIsScrolled() {
  const [isScrolled, setIsScrolled] = useState(
    () => typeof window !== 'undefined' && window.scrollY > COLLAPSE_AT
  )
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY
      setIsScrolled((prev) => (prev ? y > EXPAND_AT : y > COLLAPSE_AT))
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  return isScrolled
}

export default function Header({
  title,
  back,
  withUpdate,
}: {
  title: string
  back?: string | number
  withUpdate?: boolean
}) {
  const isMobile = useMediaQuery('(max-width: 799px)')
  const isScrolled = useIsScrolled()

  if (isMobile && withUpdate) {
    return (
      <div className={classes.update_container_mobile}>
        <div
          className={clsx(
            classes.header,
            {
              [classes.headerScrolled]: isScrolled,
            },
            classes.without_position
          )}
        >
          <HeaderBar back={back} />

          <div
            className={clsx(classes.title, classes.mobileTitle, {
              [classes.hideTitle]: isScrolled,
            })}
          >
            <Text component="h1" className={classes.title}>
              {title}
            </Text>
          </div>
        </div>

        <UpdatedBar />
      </div>
    )
  }

  if (isMobile) {
    return (
      <div
        className={clsx(
          classes.header,
          {
            [classes.arena_header]:
              title === 'Arena' || title === 'Home' || title === 'Learning',
          },
          {
            [classes.headerScrolled]: isScrolled,
          }
        )}
      >
        <HeaderBar back={back} />

        <div
          className={clsx(classes.title, classes.mobileTitle, {
            [classes.hideTitle]: isScrolled,
          })}
        >
          <div className={classes.wrapper}>
            <div className={classes.left}>
              <div className={classes.top_left} />
              <div className={classes.bottom_left} />
            </div>
            <Text component="h1" className={classes.title}>
              {title}
            </Text>
            <div className={classes.right}>
              <div className={classes.top_right} />
              <div className={classes.bottom_right} />
            </div>
          </div>
        </div>
      </div>
    )
  } else {
    return (
      <>
        <div
          className={clsx(classes.header_desktop, {
            [classes.with_update]: withUpdate || title === 'Clair',
          })}
        >
          <div className={classes.wrapper}>
            <div className={classes.left}>
              <div className={classes.top_left} />
              <div className={classes.bottom_left} />
            </div>
            <Text component="h1" className={classes.title}>
              {title}
            </Text>
            <div className={classes.right}>
              <div className={classes.top_right} />
              <div className={classes.bottom_right} />
            </div>
          </div>
        </div>
        {withUpdate && <UpdatedBar />}
      </>
    )
  }
}

export function HeaderBar({ back }: { back?: string | number }) {
  const navigate = useNavigate()
  const { data: notificationsCount } = useNotificationsCount()
  const { mutate: markAllAsRead } = useMarkAllAsRead()
  const { mutate: markAsRead } = useMarkAsRead()
  const [opened, { open, close }] = useDisclosure(false)
  const {
    data: notificationsInfinite,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotificationsInfinite()

  const notifications =
    notificationsInfinite?.pages
      .flatMap((page) => page.data)
      .filter((notification) => {
        if (notification.entity_type === 'news') {
          return notification.metadata.news_id !== undefined
        }
        return true
      }) ?? []

  function handleNotificationSelect(notification: Notification) {
    markAsRead({ notification_id: notification.notification_id })
    close()
    if (notification.entity_type === 'news') {
      navigate(`/news/${notification.metadata.news_id}`)
    } else if (notification.entity_type === 'post') {
      navigate(
        `/competitions/${notification.metadata.competition_id}/arena#post-${notification.metadata.post_id}`
      )
    } else if (notification.entity_type === 'comment') {
      navigate(
        `/competitions/${notification.metadata.competition_id}/arena#post-${notification.metadata.post_id}-comment-${notification.metadata.comment_id}`
      )
    }
  }

  return (
    <div className={classes.header_bar}>
      {back !== undefined ? (
        typeof back === 'number' ? (
          <IconChevronLeft
            className={classes.header_icon}
            onClick={() => navigate(back)}
          />
        ) : (
          <Link to={back}>
            <IconChevronLeft className={classes.header_icon} />
          </Link>
        )
      ) : (
        <ProfileIconLink />
      )}

      <div className={classes.competition_notification_wrapper}>
        <div className={classes.notificationsContainer}>
          <IconBell className={classes.header_icon} onClick={open} />
          {notificationsCount.count > 0 && (
            <div className={classes.notificationsCount} onClick={open}>
              {notificationsCount.count}
            </div>
          )}
          <Drawer.Root
            opened={opened}
            onClose={close}
            position="bottom"
            size="lg"
          >
            <DrawerOverlay backgroundOpacity={0.3} blur={2} />
            <DrawerContent className={classes.drawerContent}>
              <FocusTrap.InitialFocus />
              <DrawerHeader className={classes.drawerHeader}>
                <div className={classes.drawerHeaderWrapper}>
                  <Typography fs="lg" fw={500} lh="125">
                    Notifications
                  </Typography>
                  <div
                    style={{ cursor: 'pointer' }}
                    onClick={() => markAllAsRead()}
                  >
                    <Typography
                      fs="xs"
                      lh="150"
                      fc={{ light: 'primary-5', dark: 'primary-4' }}
                      underline
                    >
                      Mark all as read
                    </Typography>
                  </div>
                </div>
                <Drawer.CloseButton />
              </DrawerHeader>
              {notifications.length > 0 ? (
                notifications.map((notification: Notification) => (
                  <div
                    key={notification.notification_id}
                    className={classes.notificationRow}
                    data-read={!notification.is_read}
                    onClick={() => handleNotificationSelect(notification)}
                  >
                    {notification.entity_type === 'post' ||
                    notification.entity_type === 'comment' ? (
                      <AspectRatio ratio={1 / 1}>
                        <Image
                          src={notification.metadata.author_photo.thumbnail}
                          alt={notification.metadata.author_name}
                          className={classes.authorImage}
                          fit="scale-down"
                        />
                      </AspectRatio>
                    ) : (
                      <div className={classes.newsContainer}>
                        <IconNews className={classes.newsIcon} />
                      </div>
                    )}
                    <div className={classes.notificationContainer}>
                      <Typography lh="140">{notification.content}</Typography>
                    </div>
                    <IconChevronRight
                      className={classes.notificationIcon}
                      data-read={!notification.is_read}
                    />
                  </div>
                ))
              ) : (
                <Typography ta="center">No new notifications.</Typography>
              )}
              {hasNextPage && (
                <div
                  className={classes.loaderContainer}
                  onClick={() => fetchNextPage()}
                >
                  {isFetchingNextPage ? (
                    <Loader color="#0B6B7B" />
                  ) : (
                    <Typography
                      fs="xs"
                      lh="150"
                      fc={{ light: 'primary-5', dark: 'primary-4' }}
                      underline
                    >
                      Load More
                    </Typography>
                  )}
                </div>
              )}
            </DrawerContent>
          </Drawer.Root>
        </div>
      </div>
    </div>
  )
}

function ProfileIconLink() {
  const { player_id, competition_id } = useIsPlayer()
  const to = player_id
    ? `/competitions/${competition_id}/players/${player_id}`
    : '/settings'
  return (
    <Link to={to}>
      <IconUserCircle className={classes.header_icon} />
    </Link>
  )
}

export function ClairMobileHeader({
  title,
  open,
}: {
  title: string
  open: () => void
}) {
  const isScrolled = useIsScrolled()

  return (
    <div
      className={clsx(classes.header, {
        [classes.headerScrolled]: isScrolled,
      })}
    >
      <div className={classes.header_bar}>
        <ProfileIconLink />

        <div className={classes.competition_notification_wrapper}>
          <IconEdit className={classes.header_icon} />
          <IconMessages className={classes.header_icon} onClick={open} />
        </div>
      </div>

      <div
        className={clsx(classes.title, classes.mobileTitle, {
          [classes.hideTitle]: isScrolled,
        })}
      >
        <div className={classes.wrapper}>
          <div className={classes.left}>
            <div className={classes.top_left} />
            <div className={classes.bottom_left} />
          </div>
          <Text component="h1" className={classes.title}>
            {title}
          </Text>
          <div className={classes.right}>
            <div className={classes.top_right} />
            <div className={classes.bottom_right} />
          </div>
        </div>
      </div>
    </div>
  )
}
